import { createContext, useContext } from 'react';
import { supabase } from './supabase';
import type { User } from '@supabase/supabase-js';
import { monitor } from './monitoring/core';
import type { TwoFactorMethod, TwoFactorSetup } from '../types/auth';

export interface AuthState {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  setupTwoFactor: (type: TwoFactorMethod['type']) => Promise<TwoFactorSetup>;
  verifyTwoFactor: (code: string, method: TwoFactorMethod) => Promise<void>;
  disableTwoFactor: (code: string, method: TwoFactorMethod) => Promise<void>;
  getTwoFactorMethods: () => Promise<TwoFactorMethod[]>;
  generateBackupCodes: (method: TwoFactorMethod) => Promise<string[]>;
}

export const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  setupTwoFactor: async () => ({ secret: '', qr_code: '', backup_codes: [] }),
  verifyTwoFactor: async () => {},
  disableTwoFactor: async () => {},
  getTwoFactorMethods: async () => [],
  generateBackupCodes: async () => [],
});

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export async function signIn(email: string, password: string) {
  const { error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error) {
    monitor.logError('Sign in failed', { error });
    throw error;
  }
}

export async function signUp(email: string, password: string) {
  const { error } = await supabase.auth.signUp({
    email,
    password
  });
  if (error) {
    monitor.logError('Sign up failed', { error });
    throw error;
  }
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) {
    monitor.logError('Sign out failed', { error });
    throw error;
  }
}

export async function setupTwoFactor(type: TwoFactorMethod['type']): Promise<TwoFactorSetup> {
  const { data, error } = await supabase
    .functions.invoke('setup-2fa', {
      body: { type }
    });

  if (error) throw error;
  return data;
}

export async function verifyTwoFactor(code: string, method: TwoFactorMethod): Promise<void> {
  const { error } = await supabase
    .functions.invoke('verify-2fa', {
      body: { code, method_id: method.id }
    });

  if (error) throw error;
}

export async function disableTwoFactor(code: string, method: TwoFactorMethod): Promise<void> {
  const { error } = await supabase
    .functions.invoke('disable-2fa', {
      body: { code, method_id: method.id }
    });

  if (error) throw error;
}

export async function getTwoFactorMethods(): Promise<TwoFactorMethod[]> {
  const { data, error } = await supabase
    .from('two_factor_methods')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

export async function generateBackupCodes(method: TwoFactorMethod): Promise<string[]> {
  const { data, error } = await supabase
    .functions.invoke('generate-backup-codes', {
      body: { method_id: method.id }
    });

  if (error) throw error;
  return data.backup_codes;
}